import * as THREE from 'three';
import type { GameContext, RenderContext } from '../core/context';
import { EventBus } from '../core/eventBus';
import type { GameEvents } from '../core/events';
import { HookRegistry } from '../core/hooks';
import { FixedStepLoop } from '../core/loop';
import { Rng } from '../core/rng';
import { Services } from '../core/services';
import { createGameState } from '../core/state';
import type { System, SystemFactory } from '../core/system';
import { InputManager } from '../input/input';
import { PhysicsWorld } from '../physics/world';
import { createRenderer, type RendererHandle } from '../render/renderer';
import { SYSTEMS } from './systems';

export interface GameOptions {
  /** Element that receives the WebGL canvas. Omit (or headless) for tests. */
  container?: HTMLElement | null;
  /** Element for HTML UI layers. */
  uiRoot?: HTMLElement | null;
  seed?: number;
  /** No renderer, no DOM input. Used by vitest integration tests. */
  headless?: boolean;
  /** Override the system list (tests). */
  systems?: SystemFactory[];
  /**
   * Lockstep (`?lockstep` in the URL): animation frames do nothing; only `stepFrames(n)` advances the
   * simulation and runs the views (camera included). A playtest is then a pure function of the seed and
   * the hook calls, independent of wall-clock time and of how many frames the browser managed in between.
   */
  lockstep?: boolean;
}

export interface PerfStats {
  fps: number;
  frameMs: number;
  frameMsMax: number;
  simMs: number;
  updateMs: number;
  physicsMs: number;
  drawCalls: number;
  triangles: number;
  geometries: number;
  textures: number;
  bodies: number;
  awakeBodies: number;
  stepsLastFrame: number;
}

/**
 * Owns the engine objects, builds systems from the registry, and runs the loop.
 * Systems are sorted by `order` (stable for ties) and called in that order.
 */
export class Game {
  readonly ctx: GameContext;
  readonly systems: System[];
  private rendererHandle: RendererHandle | null = null;
  private rafId = 0;
  private lastTime = 0;
  private frameTimes: number[] = [];
  private simMs = 0;
  private updateMs = 0;
  private running = false;
  /** See {@link GameOptions.lockstep}. */
  lockstep: boolean;

  constructor(opts: GameOptions = {}) {
    this.lockstep = opts.lockstep ?? false;
    const headless = opts.headless ?? false;
    const camera = new THREE.PerspectiveCamera(34, 16 / 9, 1, 600);
    const scene = new THREE.Scene();
    let render: RenderContext | null = null;
    if (!headless && opts.container) {
      this.rendererHandle = createRenderer(opts.container, camera);
      render = { renderer: this.rendererHandle.renderer, canvas: this.rendererHandle.canvas };
    }
    const state = createGameState();
    const events = new EventBus<GameEvents>();
    const input = new InputManager();
    if (!headless && typeof window !== 'undefined') input.attach(window);

    const systemsRef: { list: System[] } = { list: [] };
    const loop = new FixedStepLoop({
      fixedUpdate: (dt) => {
        const t0 = performance.now();
        input.beginStep();
        for (const s of systemsRef.list) s.fixedUpdate?.(dt);
        state.time += dt;
        state.frame++;
        this.simMs += performance.now() - t0;
      },
      update: (dt, alpha) => {
        const t0 = performance.now();
        for (const s of systemsRef.list) s.update?.(dt, alpha);
        this.updateMs = performance.now() - t0;
      },
    });
    events.frameSource = () => state.frame;

    this.ctx = {
      events,
      services: new Services(),
      hooks: new HookRegistry(),
      state,
      rng: new Rng(opts.seed ?? 1971),
      input,
      physics: new PhysicsWorld(),
      loop,
      scene,
      camera,
      render,
      uiRoot: opts.uiRoot ?? null,
    };

    const factories = opts.systems ?? SYSTEMS;
    const built = factories.map((f) => f());
    // Stable sort by order.
    this.systems = built.map((s, i) => ({ s, i })).sort((a, b) => a.s.order - b.s.order || a.i - b.i).map((x) => x.s);
    systemsRef.list = this.systems;
    this.registerCoreHooks();
  }

  async init(): Promise<void> {
    for (const s of this.systems) await s.init?.(this.ctx);
    for (const s of this.systems) s.start?.();
    this.ctx.state.mode = 'playing';
  }

  /** Start the requestAnimationFrame loop (browser only). */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    const frame = (now: number): void => {
      if (!this.running) return;
      const dt = (now - this.lastTime) / 1000;
      this.lastTime = now;
      this.frame(dt);
      this.rafId = requestAnimationFrame(frame);
    };
    this.rafId = requestAnimationFrame(frame);
  }

  /** Advance one rendered frame by real dt seconds (exposed for tests). */
  frame(dt: number): void {
    // Lockstep: not even beginFrame() — it would latch (and the next rAF drop) the UI's frame presses
    // before any stepFrames() call got to read them. stepFrames latches them itself instead.
    if (this.lockstep) return;
    const input = this.ctx.input;
    input.beginFrame();
    if (input.framePressed('pause') && (this.ctx.state.mode === 'playing' || this.ctx.state.mode === 'paused')) {
      this.setPaused(this.ctx.state.mode === 'playing');
    }
    this.simMs = 0;
    this.ctx.loop.advance(dt);
    this.frameTimes.push(dt * 1000);
    if (this.frameTimes.length > 120) this.frameTimes.shift();
  }

  setPaused(paused: boolean): void {
    const st = this.ctx.state;
    if (paused === (st.mode === 'paused')) return;
    st.mode = paused ? 'paused' : 'playing';
    this.ctx.loop.paused = paused;
    this.ctx.events.emit('pauseChanged', { paused });
  }

  stop(): void {
    this.running = false;
    if (this.rafId && typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(this.rafId);
  }

  dispose(): void {
    this.stop();
    for (const s of [...this.systems].reverse()) s.dispose?.();
    this.ctx.input.detach();
    this.rendererHandle?.dispose();
    this.ctx.events.clear();
  }

  getPerf(): PerfStats {
    const ft = this.frameTimes;
    const avg = ft.length ? ft.reduce((a, b) => a + b, 0) / ft.length : 0;
    const info = this.ctx.render?.renderer.info;
    const ps = this.ctx.physics.stats;
    return {
      fps: avg > 0 ? 1000 / avg : 0,
      frameMs: avg,
      frameMsMax: ft.length ? Math.max(...ft) : 0,
      simMs: this.simMs,
      updateMs: this.updateMs,
      physicsMs: ps.stepMs,
      drawCalls: info?.render.calls ?? 0,
      triangles: info?.render.triangles ?? 0,
      geometries: info?.memory.geometries ?? 0,
      textures: info?.memory.textures ?? 0,
      bodies: ps.bodies,
      awakeBodies: ps.awake,
      stepsLastFrame: this.ctx.loop.lastSteps,
    };
  }

  /** Engine-level hooks. Modules override `setHeat` / `startMission` with real implementations. */
  private registerCoreHooks(): void {
    const { hooks, events, state, input, loop } = this.ctx;
    hooks.register('snapshot', () => ({
      state: JSON.parse(JSON.stringify(state)) as unknown,
      ...hooks.collectSnapshot(),
    }));
    hooks.register('stepFrames', (n) => {
      // In lockstep each stepFrames call is the rendered frame (see frame()), so it opens the frame edge.
      if (this.lockstep) input.beginFrame();
      loop.stepFrames(Math.max(0, Math.floor(n)));
    });
    hooks.register('events', (type) =>
      events.log.filter((e) => !type || e.type === type).map((e) => ({ frame: e.frame, type: e.type, payload: e.payload })),
    );
    hooks.register('clearEvents', () => {
      events.log.length = 0;
    });
    hooks.register('getPerf', () => ({ ...this.getPerf() }));
    hooks.register('setInput', (i) => input.setVirtual(i));
    hooks.register('clearInput', () => input.clearVirtual());
    hooks.register('press', (a) => input.pulse(a as Parameters<InputManager['pulse']>[0]));
    hooks.register('pause', (p) => this.setPaused(p));
    hooks.register('setTimeScale', (s) => {
      loop.timeScale = Math.max(0, s);
    });
    // Defaults — replaced by the heat and missions modules.
    hooks.register('setHeat', (level) => {
      const previous = state.heat;
      state.heat = Math.max(0, Math.min(4, Math.round(level)));
      if (state.heat !== previous) events.emit('heatChanged', { level: state.heat, previous });
    });
    hooks.register('startMission', (id) => {
      console.warn(`[__bh] startMission('${id}'): missions module not registered yet`);
    });
  }
}
